import { Permission } from '@contracts'
import appContext from 'context/appContext'
import { useCallback, useContext, useMemo } from 'react'

export interface PermissionOverride {
  permission: Permission
  hasPermission: boolean
}
interface UsePermissionsProps {
  clientId?: string
  permissionSubset?: Permission[]
  permissionOverrides?: PermissionOverride[]
}
// TODO: unit tests
// TODO: handle no props input

/**
 * A hook to check the permissions of the current user, for client entitied within WPP Open
 * @param clientId the client to check permissions for
 * @param permissionsSubset an array of permissions to check for the given client
 * @returns helper functions to manually check permissions for a client, memoised permissions variables for quick access, and the state of the permissions API call
 * @example
  const { clientsWithPermissions, isGetPermissionsLoading } = usePermissions({
    permissionSubset: [ 'PDP_APP_GET_REGIONS_BY', 'PDP_APP_PUT_REGIONS_BY' ],
  })
  const [clientsWithGetSingleRegion, clientsWithPutRegion] = clientsWithPermissions
  const isInitialising = isGetPermissionsLoading || isGetClientsLoading || isGetConfigLoading

* @example
  const { clientLevelPermissions } = usePermissions({
    clientId: singleProjectData?.client_id,
    permissionSubset: [ 'PDP_APP_GET_PRODUCTS', 'PDP_APP_POST_V2_PROJECTS_BY_REGIONS_BY_SUBMIT' ],
  })
  const [ canGetProducts, canPostSubmitProject ] = clientLevelPermissions
 */
const usePermissions = ({
  clientId: clientIdProp = '',
  permissionSubset: permissionSubsetProp = [],
  permissionOverrides: permissionOverridesProp = [],
}: UsePermissionsProps = {}) => {
  const { getPermissionsApiClient } = useContext(appContext)
  const { data: permissionsData, isLoading: isGetPermissionsLoading } = getPermissionsApiClient

  // Get all clients that the user has a specific set of permissions for
  const getClientsWithPermissions = (permissions: Permission[] = permissionSubsetProp): string[][] => {
    if (!permissionsData) return []
    return permissions.map(permission => {
      return (
        permissionsData.client_accounts?.flatMap(({ user_permissions, account_id }) =>
          account_id && user_permissions?.includes(permission) ? [account_id] : [],
        ) || []
      )
    })
  }

  // Get a list of all the permissions the user has, for a specific client
  const getPermissionsForClient = (clientId: string = clientIdProp): Permission[] => {
    if (!permissionsData) return []
    const clientPermissions = permissionsData.client_accounts?.find(({ account_id }) => account_id === clientId)
    return (clientPermissions?.user_permissions || []) as Permission[]
  }

  /**
   * @param permissionSet the superset of permissions
   * @param permissionsToFind the subset of permissions to cross-reference against the superset
   * @returns any queried permissions that are found in the superset
   */
  const findPermissions = (permissionSet: Permission[], permissionsToFind: Permission[]): boolean[] => {
    return permissionsToFind.map(permission => permissionSet.includes(permission))
  }

  /**
   * @param clientId the client to check permissions for
   * @param permissionSubset the subset of permissions to check against the superset of permissions for the given client
   * @returns boolean[] an array of booleans indicating whether the client has the queried permissions, in the same order as the queried permissions subset
   * @example
   *   const [canPostBrand, canPutBrand] = useMemo(
   *   () => checkPermissionsForClient(client_id || '', ['PDP_APP_POST_BRANDS', 'PDP_APP_PUT_BRANDS_BY']),
   *  [client_id, checkPermissionsForClient],
   * )
   */
  const checkPermissionsForClient = useCallback(
    (clientId: string = clientIdProp, permissionSubset: Permission[] = permissionSubsetProp) => {
      const permissionSuperSet = getPermissionsForClient(clientId)
      return findPermissions(permissionSuperSet, permissionSubset)
    },
    [clientIdProp, permissionSubsetProp],
  )

  /**
   * A helper function to allow the user to the permissions response (e.g. when a 403 error is returned for getProjects, set canGetProjects to false)
   * @param overriddenPermissions an array of permission overrides to apply to the client's permissions
   * @param permissionSubset the subset of permissions to check against the superset of permissions for the given client
   * @param clientId the client to check permissions for
   * @returns boolean[] an array of booleans indicating whether the client has the queried permissions, in the same order as the queried permissions subset
   * @example 
  const [permissionsOverrides, setPermissionsOverrides] = useState<PermissionOverride[]>([])

  const { clientLevelPermissions } = usePermissions({
    clientId: getSingleProjectData?.client_id,
    permissionSubset: ['PDP_APP_GET_V2_PROJECTS_BY'],
    permissionOverrides: permissionsOverrides,
  })

  const [canGetProject] = clientLevelPermissions

  useEffect(() => {
    getSingleProject()
      .then(project => {
        // logic
      })
      .catch(error => {
        const err = error as FEApiError
        if (err.status === 403) {
          setPermissionsOverrides([
            {
              permission: 'PDP_APP_GET_V2_PROJECTS_BY',
              hasPermission: false,
            },
          ])
        }
      })
    }, [projectId, isGetSingleProjectLoading])
    */
  const clientLevelPermissionsWithOverrides = useCallback(
    (
      overriddenPermissions: PermissionOverride[] = permissionOverridesProp,
      permissionSubset: Permission[] = permissionSubsetProp,
      clientId: string = clientIdProp,
    ) => {
      const permissionSet = checkPermissionsForClient(clientId, permissionSubset)
      overriddenPermissions.forEach(({ permission, hasPermission }) => {
        const index = permissionSubset.indexOf(permission)
        permissionSet[index] = hasPermission
      })
      return permissionSet
    },
    [clientIdProp, checkPermissionsForClient],
  )

  /**
   * @returns boolean[] an array of booleans indicating whether the client has the queried permissions, in the same order as the queried permissions subset
   * REQUIRES: clientId AND permissionSubset props to be set
   */
  const overridableClientLevelPermissions = useMemo(clientLevelPermissionsWithOverrides, [
    clientLevelPermissionsWithOverrides,
  ])

  /**
   * @returns boolean[] an array of booleans indicating whether the client has the queried permissions, in the same order as the queried permissions subset
   * REQUIRES: clientId AND permissionSubset props to be set
   */
  const clientLevelPermissions = useMemo(checkPermissionsForClient, [checkPermissionsForClient])

  /**
   * @returns string[][] a list of all clients that have a specific permission, in the same order as the queried permissions subset
   * REQUIRES: permissionSubset prop to be set
   */
  const clientsWithPermissions = useMemo(
    () => (isGetPermissionsLoading ? [] : getClientsWithPermissions()),
    [isGetPermissionsLoading],
  )

  return {
    findPermissions,
    checkPermissionsForClient,
    clientLevelPermissions,
    overridableClientLevelPermissions,
    clientsWithPermissions,
    isGetPermissionsLoading: !permissionsData || isGetPermissionsLoading,
  }
}

export default usePermissions
